import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {Button, Size, Type} from '@Components/button';
import {MystuffPopupItem} from '@Components/mystuff-popup-item/mystuff-popup-item';
import {
  useEventWizardHasUnsavedChanges,
  useEventWizardIsAITypeLoading,
  useEventWizardIsDraftBeingSaved,
  useEventWizardIsEventBeingCreated,
  useEventWizardIsSaveAsDraftOffered,
  useEventWizardReduxState,
  useEventWizardTitle,
} from '@Components/event-wizard/event-wizard.hooks';
import {onEventWizardIsDraftBeingSavedUpdate, onEventWizardIsEventBeingCreatedUpdate} from '@Components/event-wizard/event-wizard-slice';
import {saveDraftEvent} from '@Components/event-wizard/event-wizard.helper';
import {isEventNameComplete} from '@Components/event-wizard/name-input/name-input.types';
import {authenticateUser} from '@Libraries/user.library';
import {openErrorModal} from '@Modals/error-modal';
import type {SaveAsDraftActionProps} from './save-as-draft-action.types';
import {SAVE_AS_DRAFT_TOOLTIP_ID, SaveAsDraftVariant} from './save-as-draft-action.types';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {isTouchDevice} from '@Utils/browser.util';

export function EventWizardSaveAsDraftAction({variant}: SaveAsDraftActionProps): ReactElement | null {
  const dispatch = useDispatch();
  const title = useEventWizardTitle();
  const isEventBeingCreated = useEventWizardIsEventBeingCreated();
  const isDraftBeingSaved = useEventWizardIsDraftBeingSaved();
  const hasUnsavedChanges = useEventWizardHasUnsavedChanges();
  const isSaveAsDraftOffered = useEventWizardIsSaveAsDraftOffered();
  const eventWizardReduxState = useEventWizardReduxState();
  const isAITypeLoading = useEventWizardIsAITypeLoading();
  const isPublishInFlight = isEventBeingCreated && !isDraftBeingSaved;

  /**
   * Three states, and the third is why this is not just a validity check: once a draft has been saved and
   * nothing has been touched since, there is nothing left to save, so the action goes back to disabled.
   */
  const isSaveable = isEventNameComplete(title) && hasUnsavedChanges;
  const isDisabled = isPublishInFlight || !isSaveable;

  /**
   * The gate every surface answers to. `isDisabled` deliberately does NOT include a draft save in flight,
   * because the button renders that state as a spinner rather than as grey -- so a second activation was
   * reachable while a save was running. `Button` happened to survive it by self-guarding on `isLoading`;
   * the popup item had no equivalent, and a second tap re-posted `saveDraftEvent`. If that landed before
   * the response set `eventHID`, both requests were creates and the organiser got two draft rows (T3.17) --
   * the bug T3.5 exists to prevent, on the surface it did not cover.
   *
   * Declared once and consumed by `onClick` (which both variants call) and by the popup item's `disabled`,
   * so neither surface carries its own copy of the rule.
   */
  const isBlocked = isDisabled || isDraftBeingSaved;

  /**
   * One disabled button, two reasons, and telling the organiser the wrong one is worse than saying nothing:
   * somebody who has set a date but not a name HAS edited a detail, so "edit any detail" would read as a bug.
   * `hasUnsavedChanges` separates the pair, so this needs no extra state to track interaction.
   */
  const getDisabledReason = (): string => {
    if (!hasUnsavedChanges) {
      return window.i18next.t('pmwjs_edit_any_event_detail_to_save_it_as_a_draft');
    }

    return window.i18next.t('pmwjs_add_an_event_name_to_save_it_as_a_draft');
  };

  const resetSaveDraftFlags = (): void => {
    dispatch(onEventWizardIsEventBeingCreatedUpdate(false));
    dispatch(onEventWizardIsDraftBeingSavedUpdate(false));
  };

  const onSaveDraftFail = (showErrModal = false): void => {
    resetSaveDraftFlags();
    if (showErrModal) {
      openErrorModal({message: window.i18next.t('pmwjs_error_message'), log: false});
    }
  };

  /**
   * The click raises the flags; the effect below does the saving. Same split as the publish save in
   * {@link EventFooter}, and for the same reason: the AI event type is fetched on the name field's blur, so
   * the click that starts a save can land while that request is still out. Saving from the click posts the
   * event before its type arrives.
   */
  const onClick = (): void => {
    if (isBlocked) {
      return;
    }

    dispatch(onEventWizardIsDraftBeingSavedUpdate(true));
    dispatch(onEventWizardIsEventBeingCreatedUpdate(true));
  };

  /**
   * Waits for the AI event type before posting, mirroring `areRequirementsToSaveComplete()` on the publish
   * path. A draft written without its type is not just missing a field: nothing persists the type afterwards,
   * so every later open of that draft re-generates it and throws it away again.
   *
   * `hasFiredSaveRef` keeps it to one POST per click. The effect is keyed on the loading flag as well as the
   * saving flag, so it re-runs when the type lands -- without the guard that second run would post a second
   * time, and a duplicate create is exactly the failure T3.5 and T3.17 exist to prevent.
   */
  const hasFiredSaveRef = useRef(false);

  useEffect(() => {
    if (!isDraftBeingSaved) {
      hasFiredSaveRef.current = false;
      return;
    }

    if (isAITypeLoading || hasFiredSaveRef.current) {
      return;
    }

    hasFiredSaveRef.current = true;
    saveDraft();
  }, [isDraftBeingSaved, isAITypeLoading]);

  const saveDraft = (): void => {
    void authenticateUser(
      () => saveDraftEvent(eventWizardReduxState, onSaveDraftFail),
      resetSaveDraftFlags,
      {
        panelText: window.i18next.t('pmwjs_make_account_to_share_event'),
        panelSubtext: window.i18next.t('pmwjs_login_event_promotion_subtext'),
      }
    );
  };

  /**
   * `text` is hidden by the button's own loading style, so the in-progress label rides on
   * `loadingStateText`, which renders beside the spinner instead of replacing it.
   */
  const getButton = (): ReactElement => {
    return (
      <Button
        onClick={onClick}
        isLoading={isDraftBeingSaved}
        disabled={isDisabled}
        size={Size.SMALL}
        type={Type.GHOST}
        icon="icon-save"
        text={window.i18next.t('pmwjs_save_as_draft')}
        loadingStateText={window.i18next.t('pmwjs_saving')}
      />
    );
  };

  /**
   * The tooltip wraps the button rather than sitting on it: a disabled button swallows its own pointer
   * events, so a hover handler bound to it would never fire.
   *
   * `OverlayTrigger` rather than the in-house `TooltipTrigger`, which pins `left: 0` and ran this tooltip off
   * the right of the viewport -- the trigger sits at the far end of the wizard nav. Bootstrap positions
   * against the viewport itself, so the anchoring needs no per-site CSS. Usage mirrors
   * {@link EventStatsColumn}, including `click` as a trigger and the longer hide delay on touch, where there
   * is no hover and the reason would otherwise be unreachable.
   */
  const getButtonWithDisabledReason = (): ReactElement => {
    if (!isDisabled || isPublishInFlight) {
      return getButton();
    }

    const disabledReason = getDisabledReason();

    return (
      <OverlayTrigger
        overlay={
          <Tooltip id={SAVE_AS_DRAFT_TOOLTIP_ID} className="_rounded-arrow">
            {disabledReason}
          </Tooltip>
        }
        delay={{show: 200, hide: isTouchDevice() ? 1000 : 200}}
        placement="bottom"
        trigger={['focus', 'hover', 'click']}
      >
        <div tabIndex={0} aria-label={disabledReason} aria-describedby={SAVE_AS_DRAFT_TOOLTIP_ID}>
          {getButton()}
        </div>
      </OverlayTrigger>
    );
  };

  if (!isSaveAsDraftOffered) {
    return null;
  }

  if (variant === SaveAsDraftVariant.BUTTON) {
    return getButtonWithDisabledReason();
  }

  return (
    <MystuffPopupItem
      text={isDraftBeingSaved ? window.i18next.t('pmwjs_saving') : window.i18next.t('pmwjs_save_as_draft')}
      icon="icon-save"
      disabled={isBlocked}
      onClick={onClick}
      textClassName={isBlocked ? 'content-disabled' : ''}
      iconClassName={isBlocked ? 'content-disabled' : ''}
    />
  );
}
